import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { firebaseLogin } from "../api/firebaseAuth";
import { validateUser } from "../api/authApi";
import { useAuth } from "../context/AuthContext";
import PersonaSelectorModal from "./PersonaSelectorModal";
import SignUpPage from "./Login/SignUpPage";
import OrbieeLogo from "../images/brand/orbiee-logo.png";

// Demo credentials behind the "Admin login" button. Changed with the
// backend's admin account on 2026-10-19 (the user's choice to keep them in
// source, as the demo personas are); update here when the backend changes.
const DEMO_ADMIN = { username: "admin@gmail.com", password: "admin@1" };

export default function SignInModal({ onClose }) {
  const { login } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPersonaModal, setShowPersonaModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Esc closes the modal — expected of any dialog.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // 1. Firebase — get ID token.
      const token = await firebaseLogin(username, password);

      // 2. Backend validation — writes the auth cookies and returns the
      //    profile AuthContext expects, built once for both reply shapes
      //    (profileFromAuthValidate). An admin's user list is not part of
      //    it; login() fetches that.
      const profile = await validateUser(token);

      // 3. Hand off to AuthContext:
      //    - updates useAuth() state (all consumers re-render)
      //    - awaits WebSocket open
      //    - dispatches "authStateChanged" so ChatBot triggers avatar
      await login(profile);

      onClose();

      // Navigate to any pending product page without a hard reload.
      const pendingProductId = sessionStorage.getItem("pendingProductId");
      if (pendingProductId) {
        sessionStorage.removeItem("pendingProductId");
        window.location.href = `/item/${pendingProductId}`;
      }
    } catch (err) {
      setError(err.message || "Sign in failed. Please try again.");
      setIsLoading(false);
    }
  };

  const handleSelectPersona = (persona) => {
    setUsername(persona.username);
    setPassword(persona.password);
    setShowPersonaModal(false);
  };

  // Portalled to <body>. This dialog is rendered from inside TopBar, and the
  // bar carries `backdrop-filter` — which makes it the containing block for
  // its fixed-position descendants, so `inset: 0` would resolve against the
  // 68px-tall bar instead of the viewport and the card would sit half
  // off-screen.
  return createPortal(
    <div
      className="ob-modalwrap"
      role="dialog"
      aria-modal="true"
      aria-label="Sign in"
    >
      {/* Scrim */}
      <div className="ob-scrim ob-show" onClick={onClose} aria-hidden="true" />

      <div className="ob-modal ob-modal-sm relative w-full max-w-[400px]">
        {/* Pale-blue crown — the one flash of brand colour on an otherwise
            quiet card. */}
        <div className="ob-authtop">
          <span className="ob-blob" style={{ width: 180, height: 180, top: -70, right: -40, background: "rgba(255,255,255,.7)" }} />
          <span className="ob-blob" style={{ width: 150, height: 150, bottom: -80, left: -30, background: "rgba(111,212,255,.35)", animationDelay: "-6s" }} />

          <button
            onClick={onClose}
            className="ob-authclose"
            aria-label="Close"
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" className="w-[17px] h-[17px]">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>

          <img src={OrbieeLogo} alt="Orbiee — Retail assistant" className="ob-authlogo" />
          <h2 className="ob-display text-[26px] leading-tight mt-3 relative" style={{ color: "var(--ob-ink)" }}>
            Welcome back
          </h2>
          <p className="text-[12.5px] mt-1 relative" style={{ color: "var(--ob-ink-2)" }}>
            Sign in and pick up exactly where you left off.
          </p>
        </div>

        <div className="ob-authbody">
          <form onSubmit={handleSubmit} className="ob-authform">
            <div>
              <label className="ob-lbl" htmlFor="ob-signin-user">
                Username
              </label>
              <input
                id="ob-signin-user"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={`ob-field ${error ? "ob-field-err" : ""}`}
                placeholder="you@example.com"
                autoComplete="username"
                required
              />
            </div>

            <div>
              <label className="ob-lbl" htmlFor="ob-signin-pass">
                Password
              </label>
              <div className="relative">
                <input
                  id="ob-signin-pass"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`ob-field pr-11 ${error ? "ob-field-err" : ""}`}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="ob-eyebtn"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="w-[17px] h-[17px]">
                      <path d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8" />
                      <path d="M9.4 5.2A9.4 9.4 0 0112 5c5 0 9 4.5 9 7 0 .9-.5 2-1.4 3.1M6.3 6.8C3.9 8.3 3 10.3 3 12c0 2.5 4 7 9 7 1.3 0 2.5-.3 3.6-.8" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="w-[17px] h-[17px]">
                      <path d="M3 12s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7Z" />
                      <circle cx="12" cy="12" r="2.6" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* One error block for the whole form — a failed sign-in is a
                form-level outcome, not a per-field one. */}
            {error && (
              <div className="ob-authalert" role="alert">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.85} strokeLinecap="round" className="w-[15px] h-[15px] shrink-0 mt-[1px]">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v5M12 16.2v.1" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="ob-btn ob-btn-solid ob-btn-lg ob-btn-block"
            >
              {isLoading ? (
                <>
                  <span className="ob-spinner" aria-hidden="true" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <p className="ob-authalt text-[12.5px] text-center ob-ink3">
            <button
              type="button"
              onClick={() => setShowSignUpModal(true)}
              className="ob-underline font-semibold"
              style={{ color: "var(--ob-agent)" }}
            >
              Create an account
            </button>{" "}
            to explore everything.
          </p>

          <div className="ob-authsplit flex items-center gap-2">
            <span className="flex-1 h-px" style={{ background: "var(--ob-line)" }} />
            <span className="ob-eyebrow">or try a demo</span>
            <span className="flex-1 h-px" style={{ background: "var(--ob-line)" }} />
          </div>

          <div className="ob-authdemos flex gap-2">
            <button
              type="button"
              onClick={() => setShowPersonaModal(true)}
              className="ob-btn ob-btn-ghost ob-btn-sm flex-1"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" className="w-[14px] h-[14px]">
                <path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" />
                <circle cx="10" cy="8" r="3.4" />
                <path d="M19 11h2M20 10v2" />
              </svg>
              Demo login
            </button>
            <button
              type="button"
              onClick={() => {
                setUsername(DEMO_ADMIN.username);
                setPassword(DEMO_ADMIN.password);
              }}
              className="ob-btn ob-btn-ghost ob-btn-sm flex-1"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" className="w-[14px] h-[14px]">
                <path d="M12 3l7.5 3.2v5c0 4.4-3.1 8.4-7.5 9.6-4.4-1.2-7.5-5.2-7.5-9.6v-5Z" />
              </svg>
              Admin login
            </button>
          </div>

          <div className="ob-authsecure flex items-center justify-center gap-1.5 ob-ink3 text-[11px]">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="w-[13px] h-[13px]">
              <rect x="4" y="10" width="16" height="10" rx="2.5" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
            All data is kept secure
          </div>
        </div>
      </div>

      {showPersonaModal && (
        <PersonaSelectorModal
          onClose={() => setShowPersonaModal(false)}
          onSelectPersona={handleSelectPersona}
        />
      )}

      {showSignUpModal && (
        <SignUpPage
          onClose={() => setShowSignUpModal(false)}
          onSignUpSuccess={() => setShowSignUpModal(false)}
        />
      )}
    </div>,
    document.body
  );
}
